/**
 * Repräsentiert die Koordinatorenansicht im VERA8 Portal.
 * Diese Seite ermöglicht Koordinatoren die Verwaltung von Benutzerzugängen und Lerngruppen.
 */
import Veraportal from "./Veraportal";
import VeraApi from "./VeraApi";
import Modal from "./Modal";
import LerngruppeAnpassenModal from "./modals/LerngruppeAnpassenModal";
import Utilities from "./Utilities";
import {IPortalseite} from "./Interfaces";
import Tooltip from './Tooltip';

export default class Koordinatorenansicht extends Veraportal implements IPortalseite {
    /**
     * Wird beim Laden der Seite asynchron ausgeführt.
     * Initialisiert die Zugangsliste und Event-Listener für Aktionen wie das Anlegen,
     * Exportieren, Zurücksetzen von Passwörtern und Löschen von Lerngruppenzugängen.
     * @returns {Promise<boolean>} Resolves mit true, wenn der Ladevorgang erfolgreich war.
     */
    override async onSeiteLoad(): Promise<boolean> {
        // API-Instanz abrufen und Benutzerdaten laden
        const api = VeraApi.getInstance(this);
        let users = await api.fetchUserArray() ?? [];
        users = users.filter(u => u.accounttyp !== 2);

        // in NW nur Französisch 9. Klasse Lerngruppen-User anzeigen
        if (this.sessionvariablen.schule.land === 'nw')
            users = users.filter(u => u.accounttyp === 8);

        // DOM-Element für die Zugangsliste
        let zugangslisteDOM: HTMLDivElement = (document.querySelector(`#zugangsliste-koordinator`) as HTMLDivElement) ?? null;

        if (zugangslisteDOM === null)
            return false;

        // Rendert die Zugangsliste, falls Benutzer vorhanden sind
        if (users.length > 0) {
            document.querySelector(`button.userliste-herunterladen-btn`)?.classList.remove('disabled');

            zugangslisteDOM.innerHTML = `
            <div class="container">
            ${users.map(u => {
                return `
                <div class="zugang" data-userid="${u.user_id}">
                    <div class="zugangsinformationen">
                        <div class="username" data-tooltip="${Tooltip.html`
                            <p><strong>Nutzername</strong></p>
                            <p>Mit <strong>${u.username}</strong> meldet sich dieser Zugang im ${this.sessionvariablen.projekt.bezeichnung}-Portal an.</p>
                        `}">${u.username}</div>
                        ${(u.email !== null) ? `<div class="email" data-tooltip="${Tooltip.html`
                            <p><strong>E-Mail-Adresse</strong></p>
                            <p>Für diesen Zugang hinterlegte E-Mail-Adresse.</p>
                        `}">${u.email}</div>` : ''}
                        ${([5, 8].includes(u.accounttyp)) ? `<div class="lerngruppe" data-tooltip="${Tooltip.html`
                            <p><strong>Zugewiesene Lerngruppe</strong></p>
                            <p><strong>${u.bezeichnung}</strong> (${Utilities.printFachLang(u.kurs_fach)})</p>
                        `}">${u.bezeichnung} (${Utilities.printFachLang(u.kurs_fach)})</div>` : ''}
                    </div>
                    <div class="accounttyp">
                        ${(u.accounttyp === 1) ? `Lehrkraftzugang` : ``}
                        ${(u.accounttyp === 2) ? `Koordinatorenzugang` : ``}
                        ${(u.accounttyp === 5) ? `Lerngruppenzugang` : ``}
                        ${(u.accounttyp === 8) ? `Französisch Lerngruppe<br/>(9. Klassenstufe)` : ``}
                    </div>
                    <div class="bearbeitungsinformationen">
                        <div class="hinzugefuegt-am">erstellt am: ${new Date(u.hinzugefuegt_am).toLocaleString('de-DE', {year: '2-digit', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'})}</div>
                        ${(u.passwort_anforderung_hash !== null) ? `<div class="aktualisiert-am">Passwort geändert am: ${new Date(u.aktualisiert_am).toLocaleString('de-DE', {year: '2-digit', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'})}</div>` : ``}
                        ${(u.passwort_anforderung_hash === null) ? `<div class="passwortlink-genutzt"><b>Passwort abgerufen am: ${new Date(u.aktualisiert_am).toLocaleString('de-DE', {year: '2-digit', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'})}</b></div>` : ``}
                    </div>
                    <div class="optionen">
                        <button class="secondary passwort-neu-generieren-btn">
                            <svg role='graphics-symbol'>
                                <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#arrow-clockwise'/>
                            </svg>
                            Passwort neu generieren
                        </button>
                        <button class="secondary zugang-loeschen-btn">
                            <svg role='graphics-symbol'>
                                <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#trash'/>
                            </svg>
                            Lerngruppe löschen
                        </button>
                    </div>
                </div>
            `;
            }).join('')}
            </div>
            `;
        } else {
            // Nachricht anzeigen, wenn keine zusätzlichen Zugänge erstellt wurden
            zugangslisteDOM.innerHTML = `<span>Es wurden noch keine zusätzlichen Zugänge erstellt. Erstellen Sie weitere Zugänge und distribuieren Sie die Zugangsdaten im Kollegium.</span>`;
        }

        // Event-Listener für den Button "Lerngruppenzugang anlegen"
        document.querySelector(`button.zugang-hinzufuegen-btn`)?.addEventListener('click', () => {
            let modal = new LerngruppeAnpassenModal(this);
            modal.show({
                id: "modal-lerngruppe-anpassen",
                title: "Lerngruppenzugang anlegen",
                hasSubmit: true,
                hasCancel: true,
                hasBestaetigung: false,
                cancelText: "Abbrechen",
                submitText: "Lerngruppenzugang anlegen",
                width: 800
            });
        });

        // Event-Listener für den Button "Userliste herunterladen"
        document.querySelector(`button.userliste-herunterladen-btn`)?.addEventListener('click', () => {
            // Exportiert alle Benutzer (außer Koordinatoren) als CSV
            Utilities.exportJsonToCsv(users.map(u => {
                return {
                    Fach: Utilities.printFachLang(u.kurs_fach),
                    Lerngruppe: u.bezeichnung,
                    Nutzer: u.username,
                    Passwortlink: (u.passwort_anforderung_hash !== null) ? `https://${this.sessionvariablen.projekt.domain}/passwort-neu-generieren.php?hash=${u.passwort_anforderung_hash}` : `Passwortlink wurde bereits genutzt`,
                    Link_erstellt_am: new Date(u.aktualisiert_am).toLocaleString('de-DE', {year: '2-digit', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'})
                }
            }), `vera8-${new Date().getFullYear().toString()}-lerngruppenzugaenge.csv`, '\t');
        });

        // Event-Listener für alle Buttons "Passwort neu generieren"
        document.querySelectorAll(`button.passwort-neu-generieren-btn`)
            .forEach(b => b
                .addEventListener('click', async (evt) => {
                    let userId: number = parseInt((evt.target as HTMLButtonElement).closest(`div.zugang[data-userid]`)?.getAttribute('data-userid')) ?? null
                    if (userId === null)
                        return;

                    const api = VeraApi.getInstance(this);
                    let response = await api.passwortlinkErneutAnfordern(userId);
                    if (response === null)
                        return false;
                    if (response.bezeichnung === undefined)
                        return false;

                    const subject = `${this.sessionvariablen.projekt.bezeichnung} - Lerngruppenzugang Lerngruppe '${response.bezeichnung}'`;
                    const body = Utilities.multilineText(`
                    Liebe Lehrkraft,
                    
                    Sie erhalten mit dieser Mail Ihren persönlichen Lerngruppenzugang zum ${this.sessionvariablen.projekt.bezeichnung}-Portal für den ${this.sessionvariablen.projekt.bezeichnung}-Durchgang 2025/2026.
                    
                    Die durch den Schulkoordinator Ihnen zugewiesene Lerngruppe '${response.bezeichnung}' (${Utilities.printFachLang(response.kurs_fach)}) kann ab sofort unter
                    https://${this.sessionvariablen.projekt.domain}/ verwaltet werden.
                    
                    Loggen Sie sich dazu mit dem Nutzernamen '${response.username}' und dem Passwort ein.
                    
                    Ihr persönliches Passwort erhalten Sie einmalig unter dem folgenden Link:
                    https://${this.sessionvariablen.projekt.domain}/passwort-neu-generieren.php?hash=${response.passwort_anforderung_hash} (Link erzeugt am ${new Date(response.aktualisiert_am).toLocaleString('de-DE', {year: '2-digit', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'})})
                    
                    Bitte notieren Sie sich Ihr Passwort und verwahren Sie es sicher. Sollte der einmalige Passwortlink nicht mehr gültig sein - z.B. weil er fälschlicherweise durch Sie oder Dritte genutzt wurde, kann Ihr Koordinator einen neuen Passwortlink generieren und diesen erneut an Sie weiterleiten.
                    
                    Wenden Sie sich bei Fragen zuerst an den an Ihrer Schule zuständigen Koordinator und anschließend gerne an den ${this.sessionvariablen.projekt.bezeichnung}-Support unter ${(this.sessionvariablen.projekt.weiche !== 'vera5') ? `support@projekt-vera.de` : `support@lernstand5.de`} .
                    `);

                    let hinweisModal = new Modal(this);
                    await hinweisModal.show({
                        title: `Passwortlink distribuieren`,
                        body: `
                    <p>
                        Die Lerngruppe <i>${response.bezeichnung}</i> wurde erstellt und kann mit dem Lerngruppenzugang <i>${response.username}</i> durch die verantwortliche Lehrkraft verwaltet werden.
                        Übermitteln Sie dazu die folgenden Zugangsdaten an die entsprechende Lehrperson:
                    </p>
                    <p>
                        <b>Lerngruppe:</b> ${response.bezeichnung}<br/>
                        <b>Nutzername:</b> ${response.username}<br/><br/>
                        <b>Link zum Erhalt des persönlichen Passwortes:</b><br/>
                        <span href="https://${this.sessionvariablen.projekt.domain}/passwort-neu-generieren.php?hash=${response.passwort_anforderung_hash}" style="font-size: .6em;">https://${this.sessionvariablen.projekt.domain}/passwort-neu-generieren.php?hash=${response.passwort_anforderung_hash}</span>
                    </p>
                    <a role="button" href="mailto:${(response.email !== null) ? response.email : ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}">
                        <svg role='graphics-symbol'>
                            <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#envelope'/>
                        </svg>
                        per Mail an Lehrkraft<br/>übermitteln ...
                    </a>
                `,
                        hasSubmit: true,
                        hasCancel: false,
                        hasBestaetigung: false,
                        width: 800,
                        cancelText: 'OK'
                    });
                    await Utilities.delay(1000);
                    window.location.reload();
                })
            );

        // Event-Listener für alle Buttons "Lerngruppe löschen"
        document.querySelectorAll(`button.zugang-loeschen-btn`)
            .forEach(b => b
                .addEventListener('click', async (e) => {
                    const userId: number = parseInt(
                        (e.target as HTMLElement)
                            .closest(`div.zugang[data-userid]`)
                            ?.getAttribute('data-userid')
                    ) ?? null;

                    const lerngruppenName: string = (e.target as HTMLDivElement)
                        .closest(`div.zugang[data-userid]`)
                        .querySelector(`div.lerngruppe`)
                        .innerHTML ?? '';

                    const warnungModal = new Modal(this);
                    const warnungModalAntwort = await warnungModal.show({
                        title: `Soll diese Lerngruppe wirklich gelöscht werden?`,
                        body: `<p>
                            Möchten Sie die Lerngruppe <i>"${lerngruppenName}"</i> mitsamt allen Schüler- und Ergebnisdaten wirklich löschen?
                            Dieser Vorgang <b>kann nicht rückgängig gemacht werden</b>.<br/>
                            Geben Sie im Bestätigungsfeld <i>'OK'</i> ein, um zu bestätigen, dass Sie die Aktion wirklich durchführen möchten.
                        </p>`,
                        hasSubmit: true,
                        hasCancel: true,
                        hasBestaetigung: true,
                        width: 600,
                        cancelText: 'Abbrechen',
                        submitText: 'Bestätigen'
                    });

                    if (warnungModalAntwort === false) {
                        return;
                    }

                    await api.zugangLoeschen(userId);
                    await Utilities.delay(1000);
                    window.location.reload();
                })
            );

        return true;
    }

    /**
     * Wird ausgeführt, nachdem die Seite vollständig geladen und gerendert wurde.
     * @returns {Promise<boolean>} Resolves mit true.
     */
    override async onSeiteFinished(): Promise<boolean> {
        return true;
    }

    /**
     * Wird ausgeführt, bevor die Seite verlassen wird.
     * @returns {Promise<boolean>} Resolves mit true.
     */
    override async onSeiteLeave(): Promise<boolean> {
        return true;
    }

}
